import { useState, useRef, useEffect } from 'react';

const downsampleBuffer = (buffer, originalSampleRate, targetSampleRate) => {
  if (originalSampleRate === targetSampleRate) {
    return buffer;
  }

  const ratio = originalSampleRate / targetSampleRate;
  const newLength = Math.round(buffer.length / ratio);
  const result = new Float32Array(newLength);

  for (let i = 0; i < newLength; i++) {
    const position = Math.round(i * ratio);
    result[i] = buffer[position];
  }

  return result;
};

const floatTo16BitPCM = (input) => {
  const output = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    output[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
  }
  return output.buffer;
};

const useVoiceRecording = (socketRef, transcriptionLogRef, transcriptionTextRef, isRecording, setIsRecording) => {
  const [audioLevel, setAudioLevel] = useState(0);

  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const processorRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const isCleaningUpRef = useRef(false);
  const transcriptionBufferRef = useRef('');

  const updateAudioLevel = () => {
    if (!analyserRef.current || !isRecording) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);

    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i];
    }
    const average = sum / dataArray.length;
    const normalizedLevel = average / 255;

    setAudioLevel(normalizedLevel);

    animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
  };

  const handleStartRecording = async () => {
    if (isRecording) return;

    try {
      // Connect the socket only when recording starts
      if (socketRef.current && !socketRef.current.connected) {
        socketRef.current.connect();
      }

      if (socketRef.current && socketRef.current.resetAccumulatedText) {
        socketRef.current.resetAccumulatedText();
      }

      transcriptionBufferRef.current = '';

      setIsRecording(true);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;

      try {
        await audioContext.audioWorklet.addModule('/audio-processor.js');
      } catch (workletError) {
        throw new Error("Failed to initialize audio processing");
      }

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      const workletNode = new AudioWorkletNode(audioContext, 'audio-processor');

      workletNode.port.onmessage = (event) => {
        try {
          const inputData = event.data.audioData;
          const originalSampleRate = audioContext.sampleRate;
          const targetSampleRate = 16000;
          const downsampled = downsampleBuffer(inputData, originalSampleRate, targetSampleRate);
          const pcmBuffer = floatTo16BitPCM(downsampled);
          const binaryData = new Uint8Array(pcmBuffer);

          if (socketRef.current) {
            socketRef.current.emit("audio_stream", binaryData);
          }
        } catch (err) { }
      };

      source.connect(workletNode);
      workletNode.connect(audioContext.destination);
      processorRef.current = workletNode;

      animationFrameRef.current = requestAnimationFrame(updateAudioLevel);

    } catch (error) {
      setIsRecording(false);
      alert("Could not access microphone. Please check your permissions and try again.");
    }
  };

  const handleStopRecording = () => {
    if (!isRecording || isCleaningUpRef.current) return;

    setIsRecording(false);
    isCleaningUpRef.current = true;

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (processorRef.current) {
      try {
        processorRef.current.disconnect();
        processorRef.current.port.postMessage({ terminate: true });
      } catch (err) { }
      processorRef.current = null;
    }

    if (mediaStreamRef.current) {
      try {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      } catch (err) { }
      mediaStreamRef.current = null;
    }

    if (audioContextRef.current) {
      try {
        if (audioContextRef.current.state !== 'closed') {
          audioContextRef.current.close()
            .then(() => { })
            .catch(err => { })
            .finally(() => {
              audioContextRef.current = null;
              isCleaningUpRef.current = false;
            });
        } else {
          audioContextRef.current = null;
          isCleaningUpRef.current = false;
        }
      } catch (err) {
        audioContextRef.current = null;
        isCleaningUpRef.current = false;
      }
    } else {
      isCleaningUpRef.current = false;
    }

    if (socketRef.current) {
      socketRef.current.emit("stop_audio");
      
      // Disconnect socket after stopping recording
      if (socketRef.current.connected) {
        socketRef.current.disconnect();
      }
    }

    setAudioLevel(0);
  };

  useEffect(() => {
    if (!socketRef.current) return;

    const handleTranscription = (data) => {
      try {
        if (data && data.text) {
          if (transcriptionBufferRef.current === '') {
            transcriptionBufferRef.current = data.text;
          } else {
            transcriptionBufferRef.current += ' ' + data.text;
          }

          if (typeof transcriptionTextRef.current === 'function') {
            transcriptionTextRef.current(transcriptionBufferRef.current);
          }

          if (Array.isArray(transcriptionLogRef.current)) {
            transcriptionLogRef.current.push(data);
          } else {
            transcriptionLogRef.current = [data];
          }
        }
      } catch (error) { }
    };

    socketRef.current.on("transcription_result", handleTranscription);

    return () => {
      socketRef.current.off("transcription_result", handleTranscription);
    };
  }, [socketRef, transcriptionTextRef, transcriptionLogRef]);

  useEffect(() => {
    return () => {
      if (isRecording && !isCleaningUpRef.current) {
        handleStopRecording();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isRecording]);

  return {
    audioLevel,
    handleStartRecording,
    handleStopRecording
  };
};

export default useVoiceRecording;